import React, { useMemo, useState, useEffect } from "react";
import { GetBotStatusData } from "../apis/get";

// -------- Types --------
export type BotRunRow = {
  id: number | string;
  timestamp: string; // ISO
  status: string;
  message: string;
  city: string;
  botType: string;
};

export type BotRunGroupMap = Record<string, BotRunRow[]>;

type TabButtonProps = {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
};

type DataTableProps = {
  rows: BotRunRow[];
};

type BotRunStatusTabsProps = {
  initialData?: BotRunGroupMap; // optional SSR/seed
  endpoint?: string;            // override endpoint if needed
};

// -------- Utils --------
function classNames(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function humanizeLabel(label: string) {
  return label.replace(/_/g, " ");
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso ?? "");
  return d.toLocaleString(); // uses user’s locale/timezone
}

function statusPillClasses(status: string) {
  const s = status.toUpperCase();
  if (s.includes("ERROR") || s.includes("FAILED") || s.includes("FAIL")) {
    return "bg-red-50 text-red-700 border-red-200";
  }
  if (s.includes("EMAIL")) {
    return "bg-blue-50 text-blue-700 border-blue-200";
  }
  if (s.includes("SUCCESS")) {
    return "bg-green-50 text-green-700 border-green-200";
  }
  if (s.includes("WARN")) {
    return "bg-yellow-50 text-yellow-700 border-yellow-200";
  }
  return "bg-gray-50 text-gray-700 border-gray-200";
}

// -------- UI Pieces --------
function TabButton({ label, count, isActive, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "px-3 py-2 rounded-xl text-sm font-medium border",
        isActive
          ? "bg-black text-white border-black shadow"
          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
      )}
      aria-pressed={isActive}
    >
      <span className="capitalize">{humanizeLabel(label)}</span>
      <span
        className={classNames(
          "ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs border",
          isActive
            ? "bg-white text-black border-transparent"
            : "bg-gray-100 text-gray-700 border-gray-200"
        )}
      >
        {count}
      </span>
    </button>
  );
}

function DataTable({ rows }: DataTableProps) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const id = String(r.id ?? "").toLowerCase();
      const city = String(r.city ?? "").toLowerCase();
      const status = String(r.status ?? "").toLowerCase();
      const msg = String(r.message ?? "").toLowerCase();
      return (
        id.includes(needle) ||
        city.includes(needle) ||
        status.includes(needle) ||
        msg.includes(needle)
      );
    });
  }, [q, rows]);

  return (
    <div className="mt-4">
      <div className="mb-3 flex items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by Status, Message, City, Id…"
          className="w-full md:w-96 rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
          aria-label="Search rows"
        />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Id</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Timestamp</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Message</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">City / File</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {filtered.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{r.id}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{formatDate(r.timestamp)}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={classNames(
                    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border",
                    statusPillClasses(r.status)
                  )}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">{r.message || "—"}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{r.city?.trim() ? r.city : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="mt-4 text-sm text-gray-500">No results.</div>
      )}
    </div>
  );
}

// -------- Main Component --------
export default function BotRunStatusTabs({
  initialData,
  endpoint = "http://localhost:5001/admin/bot-run-status",
}: BotRunStatusTabsProps) {
  const [data, setData] = useState<BotRunGroupMap>(initialData ?? {});
  const keys = useMemo(() => Object.keys(data ?? {}), [data]);
  const [active, setActive] = useState<string>(keys[0] ?? "");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // keep active valid if keys change
  useEffect(() => {
    if (!active || !keys.includes(active)) setActive(keys[0] ?? "");
  }, [keys, active]);

  const counts = useMemo<Record<string, number>>(() => {
    const c: Record<string, number> = {};
    keys.forEach((k) => (c[k] = Array.isArray(data[k]) ? data[k].length : 0));
    return c;
  }, [data, keys]);

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      const res = await GetBotStatusData();
      console.log("BOt Status:", res);
      if (res.status) {
        const json: BotRunGroupMap = await res.data;
        setData(json ?? {});
        setLastUpdated(new Date());
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl p-4 md:p-8">
      <header className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Bot Run Status</h1>
          <p className="text-sm text-gray-600">
            Grouped by <code className="bg-gray-100 rounded px-1">botType</code>. Click a tab to switch.
          </p>
          {lastUpdated && (
            <p className="mt-1 text-xs text-gray-500">
              Last updated: {lastUpdated.toLocaleString()}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className={classNames(
              "inline-flex items-center rounded-xl border px-3 py-2 text-sm font-medium",
              loading
                ? "bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed"
                : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
            )}
            aria-busy={loading}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25" />
                  <path d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="4" fill="none" />
                </svg>
                Refreshing…
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <path d="M3 12a9 9 0 1 1 3 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Refresh
              </span>
            )}
          </button>

          {/* Optional: load on mount if no initialData */}
          {!initialData && (
            <button
              type="button"
              onClick={() => {
                setData({});
                setActive("");
              }}
              className="inline-flex items-center rounded-xl border px-3 py-2 text-sm font-medium bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
              title="Clear data"
            >
              Clear
            </button>
          )}
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Failed to refresh: {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {keys.map((k) => (
          <TabButton
            key={k}
            label={k}
            count={counts[k] ?? 0}
            isActive={active === k}
            onClick={() => setActive(k)}
          />
        ))}
        {keys.length === 0 && (
          <span className="text-sm text-gray-500">No groups available. Click Refresh to load.</span>
        )}
      </div>

      {/* Panel */}
      <section className="mt-4">
        {active ? (
          <DataTable rows={data[active] ?? []} />
        ) : keys.length > 0 ? (
          <div className="text-sm text-gray-500">Select a tab to view data.</div>
        ) : null}
      </section>
    </div>
  );
}
